import { DonutChartSlice } from '@/components/charts/DonutChart.tsx';
import { CHART_SERIES_COLORS } from '@/components/ui/chartPalette.ts';
import { STATUS_TONE_COLORS } from '@/components/ui/statusTone.ts';
import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { BreakdownDto, MoneyBreakdownDto } from '@/types/stats/BreakdownDto.ts';
import {
  StatSegmentDto,
  StatSegmentType,
} from '@/types/stats/StatSegmentDto.ts';
import {
  SegmentPresentation,
  StatSegmentRegistry,
} from '@/types/stats/StatSegmentRegistry.ts';
import { reportStatsContractBreach } from '@/types/stats/statsContract.ts';
import { formatNumber, formatPrice } from '@/utils/formatters.ts';

export interface BreakdownSegment {
  id: string;
  label: string;
  /** Skąd ta kwota — „12 wpłat”. Pusty napis znika, nie zostawia dziury. */
  note: string;
  color: string;
  amount: MoneyValueDto;
}

/**
 * Klucz nieznany rejestrowi jest błędem kontraktu, a nie danymi do pominięcia:
 * cicho zgubiony odcinek rozjeżdża sumę paska i nikt tego nie zauważy.
 */
const presentation = (
  key: string,
  registry: StatSegmentRegistry,
): SegmentPresentation => {
  const entry = registry[key];
  if (entry) return entry;

  reportStatsContractBreach(
    `Nieznany klucz segmentu statystyk: "${key}". Dopisz go do rejestru widgetu.`,
  );
  return { label: key, tone: 'neutral' };
};

/**
 * Tożsamość odcinka rozstrzygnięta raz: rejestr pytamy jeden raz na segment,
 * bo osobne wyliczanie napisu i barwy zgłaszałoby ten sam rozjazd kontraktu
 * dwukrotnie.
 *
 * `index` jest potrzebny, bo katalog bez barw (opcje, miasta) przysyła
 * `color: null` — kolor bierze się wtedy z palety, po pozycji w rozbiciu.
 */
export const resolveSegmentIdentity = (
  segment: StatSegmentDto,
  registry: StatSegmentRegistry,
  index: number,
): { id: string; label: string; color: string } => {
  if (segment.type === StatSegmentType.LABELED)
    return {
      id: segment.id,
      label: segment.label,
      color:
        segment.color ??
        CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length],
    };

  const { label, tone } = presentation(segment.key, registry);
  return { id: segment.key, label, color: STATUS_TONE_COLORS[tone].dot };
};

export const breakdownAmount = (
  breakdown: MoneyBreakdownDto,
  value: number,
): MoneyValueDto => ({ amount: value, currency: breakdown.currency });

/** Rozbicie na wiersze legendy paska (`SegmentBreakdown`). */
export const breakdownToSegments = (
  breakdown: MoneyBreakdownDto,
  registry: StatSegmentRegistry,
): BreakdownSegment[] =>
  breakdown.segments.map((segment, index) => ({
    ...resolveSegmentIdentity(segment, registry, index),
    note: '',
    amount: breakdownAmount(breakdown, segment.value),
  }));

/**
 * Rozbicie na plasterki pierścienia. Rejestr jest opcjonalny, bo rozbicia
 * katalogowe (kategorie kosztów) niosą własne nazwy i kolory.
 */
export const breakdownToSlices = (
  breakdown: BreakdownDto,
  registry: StatSegmentRegistry = {},
): DonutChartSlice[] =>
  breakdown.segments.map((segment, index) => ({
    ...resolveSegmentIdentity(segment, registry, index),
    value: segment.value,
    formattedValue:
      breakdown.currency === null
        ? formatNumber(segment.value)
        : formatPrice(segment.value, breakdown.currency),
  }));

/** Wartość jednego stanu — wielka liczba karty bierze się właśnie stąd. */
export const keyedSegmentValue = (
  breakdown: BreakdownDto,
  key: string,
): number =>
  breakdown.segments.find(
    (segment) => segment.type === StatSegmentType.KEYED && segment.key === key,
  )?.value ?? 0;

/**
 * Największy odcinek — „najczęściej dokupywana opcja”, „najliczniejsze miasto”.
 * Przy remisie wygrywa pierwszy, bo backend porządkuje segmenty malejąco.
 *
 * Same zera to brak zwycięzcy, a nie zwycięzca z wynikiem zero: nikt niczego
 * nie dokupił, więc karta ma pokazać pusty znak, a nie pierwszą opcję z listy.
 */
export const largestSegment = (
  breakdown: BreakdownDto,
  registry: StatSegmentRegistry = {},
): { id: string; label: string; color: string; value: number } | null => {
  const winner = breakdown.segments.reduce<{
    segment: StatSegmentDto;
    index: number;
  } | null>(
    (best, segment, index) =>
      best === null || segment.value > best.segment.value
        ? { segment, index }
        : best,
    null,
  );

  if (winner === null || winner.segment.value <= 0) return null;

  return {
    ...resolveSegmentIdentity(winner.segment, registry, winner.index),
    value: winner.segment.value,
  };
};
